import React, { useState } from "react";
import { cn } from "./ui/utils";
import { proxiedMediaUrl } from "../utils/mediaUrl";
import { WhiteLogoPlate } from "./WhiteLogoPlate";

const GRADIENTS = [
  "from-indigo-500 to-purple-600",
  "from-pink-500 to-rose-600",
  "from-teal-500 to-cyan-600",
  "from-amber-500 to-orange-600",
  "from-violet-500 to-indigo-600",
  "from-emerald-500 to-teal-600",
  "from-sky-500 to-blue-600",
  "from-red-500 to-pink-600",
];

/** Huge base64 logos in list JSON freeze MAX WebView — fall back to initials. */
const MAX_DATA_URI_CHARS = 80_000;

interface CompanyAvatarProps {
  name: string;
  logoUrl?: string | null;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  className?: string;
}

function resolveAvatarSrc(logoUrl?: string | null): string | null {
  const raw = logoUrl?.trim();
  if (!raw) return null;
  if (raw.startsWith("data:")) {
    return raw.length <= MAX_DATA_URI_CHARS ? raw : null;
  }
  return proxiedMediaUrl(raw) || raw;
}

export function CompanyAvatar({ name, logoUrl, size = "md", className }: CompanyAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const idx =
    name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % GRADIENTS.length;
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const sizes = {
    sm: "w-8 h-8 text-[11px]",
    md: "w-10 h-10 text-[13px]",
    lg: "w-12 h-12 text-[15px]",
    xl: "w-16 h-16 text-[18px]",
    "2xl": "w-20 h-20 text-[22px] rounded-2xl",
  };

  const src = resolveAvatarSrc(logoUrl);
  const showLogo = Boolean(src && !imgFailed);

  if (!showLogo) {
    return (
      <div
        className={cn(
          "rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br text-white",
          GRADIENTS[idx],
          sizes[size],
          className
        )}
      >
        {initials}
      </div>
    );
  }

  return (
    <WhiteLogoPlate
      className={cn(
        "rounded-xl flex-shrink-0 shadow-sm border border-gray-100",
        sizes[size],
        className
      )}
    >
      <img
        src={src!}
        alt={name}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-contain p-0.5"
        onError={() => setImgFailed(true)}
      />
    </WhiteLogoPlate>
  );
}
